/**
 * Power BI tablo görselinin karşılığı — kütüphaneye projeden eklenen tip.
 *
 * Kütüphanenin `table` tipi boyut sütunlarına başlık veremiyor, sütun başına
 * hizalama/genişlik almıyor ve satırları toplamıyor. Rapordaki tablolar ise
 * ham satırları bazı sütunlara göre GRUPLAYIP geri kalanları topluyor (Power
 * BI'daki "Toplam" / "En küçük" seçimi). Gruplama burada, slicer'lar
 * uygulandıktan SONRA yapılır — Dashboard bileşene süzülmüş satırları verir,
 * böylece bir malzeme seçildiğinde toplamlar da ona göre değişir.
 *
 * options:
 *   columns  [{ key, label, agg?, align?, width?, format? }]
 *            agg    "sum" | "min" | "first" | "join"   (varsayılan "first")
 *                   join: gruptaki farklı değerleri virgülle yan yana yazar —
 *                   aynı tanımı paylaşan birden çok malzeme kodu için.
 *            format "int" | "text"                      (varsayılan "text")
 *   groupBy  [key] — verilmezse satırlar olduğu gibi basılır
 *   cellStyle / headerStyle  { size }
 */
import { registerChart, useChartTheme } from "@smartopt/charts";

export const PANO_TABLE = "pano-table";

const sayi = (v) => Number(v) || 0;

/** Bir grubun tek sütun değeri. */
function topla(rows, col) {
  const vals = rows.map((r) => r[col.key]);
  switch (col.agg) {
    case "sum":
      return vals.reduce((a, v) => a + sayi(v), 0);
    case "min": {
      const dolu = vals.filter((v) => v != null && v !== "");
      if (!dolu.length) return "";
      // Sayısal sütunda sayı, metin/saat sütununda sözlük sırası — Power
      // BI'daki MIN de metinde alfabetik çalışır.
      return dolu.every((v) => typeof v === "number")
        ? Math.min(...dolu)
        : dolu.reduce((a, v) => (String(v) < String(a) ? v : a));
    }
    case "join":
      return [...new Set(vals.filter((v) => v != null && v !== ""))].join(", ");
    case "first":
    default:
      return vals[0];
  }
}

/** Satırları `groupBy` sütunlarına göre toplar; grup sırası ilk görülme sırası. */
function grupla(data, groupBy, columns) {
  if (!groupBy?.length) return data;
  const gruplar = new Map();
  for (const r of data) {
    const id = groupBy.map((k) => String(r[k] ?? "")).join("\u0000");
    const g = gruplar.get(id);
    if (g) g.push(r);
    else gruplar.set(id, [r]);
  }
  return [...gruplar.values()].map((rows) => {
    const out = {};
    for (const c of columns) out[c.key] = topla(rows, c);
    return out;
  });
}

function PanoTable({ config, data }) {
  const theme = useChartTheme();
  const o = config.options ?? {};
  const columns = o.columns ?? [];
  const rows = grupla(data ?? [], o.groupBy, columns);

  const fontSize = o.cellStyle?.size ?? 12;
  const headerSize = o.headerStyle?.size ?? fontSize + 1;
  const fmt = new Intl.NumberFormat(theme.locale, { maximumFractionDigits: 0 });

  const yaz = (c, v) => {
    if (v == null || v === "") return "";
    return c.format === "int" ? fmt.format(sayi(v)) : String(v);
  };

  return (
    <div
      className="pano-table"
      style={{ height: o.height === "100%" ? "100%" : o.height ?? 320, overflow: "auto" }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
          fontSize,
          tableLayout: "auto",
        }}
      >
        <colgroup>
          {columns.map((c) => (
            <col key={c.key} style={c.width ? { width: c.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  background: theme.surface,
                  color: theme.text,
                  fontSize: headerSize,
                  fontWeight: 700,
                  padding: "6px 8px",
                  borderBottom: `2px solid ${theme.border}`,
                  textAlign: c.align ?? "left",
                  whiteSpace: "nowrap",
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                style={{ padding: 12, color: theme.muted, textAlign: "center" }}
              >
                Veri yok
              </td>
            </tr>
          )}
          {rows.map((r, i) => (
            // Rapordaki gibi satırlar iki tonda — uzun tabloda göz satırı
            // kaybetmesin.
            <tr
              key={i}
              style={{ background: i % 2 ? "rgba(255,255,255,0.05)" : "transparent" }}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  style={{
                    padding: "4px 8px",
                    borderBottom: `1px solid ${theme.grid}`,
                    color: theme.text,
                    textAlign: c.align ?? "left",
                    whiteSpace: c.wrap ? "normal" : "nowrap",
                  }}
                >
                  {yaz(c, r[c.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

registerChart(PANO_TABLE, PanoTable);
